import { useState } from "react";
import { useAuthStore } from "../stores/authStore";

const DISMISS_KEY = "wash_sale_banner_dismissed";

function isWashSaleSeason(): boolean {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed
  const day = now.getDate();

  // Active November 1 (month=10) through December 15 (month=11, day<=15)
  if (month === 10) return true; // All of November
  if (month === 11 && day <= 15) return true; // Dec 1-15
  return false;
}

function getDismissKey(): string {
  // Include the year so dismissal resets each year
  return `${DISMISS_KEY}_${new Date().getFullYear()}`;
}

export function WashSaleBanner() {
  const user = useAuthStore((s) => s.user);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(getDismissKey()) === "1",
  );

  if (!user || !isWashSaleSeason() || dismissed) return null;

  return (
    <div className="bg-amber-600 dark:bg-amber-500/10 border-b border-amber-700 dark:border-amber-500/30 px-4 py-3 flex items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <svg
          className="w-4 h-4 text-white dark:text-amber-400 flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <div className="text-xs sm:text-sm text-white dark:text-amber-200 space-y-1">
          <p>
            <strong>Year-End Tax Reminder:</strong> Consider liquidating
            automated strategies before December 1st and re-enabling them in the
            second week of January to maintain 30+ days of separation and avoid{" "}
            <a
              href="https://www.irs.gov/publications/p550#en_US_2023_publink100010601"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-white dark:text-amber-100 hover:text-amber-100 dark:hover:text-white font-medium"
            >
              wash sale
            </a>{" "}
            implications. The wash sale rule disallows a tax loss deduction if
            you sell a security at a loss and buy a substantially identical
            security within 30 days before or after the sale, which can
            eliminate tax benefits and potentially increase your tax liability.
          </p>
          <p className="text-[10px] sm:text-xs text-white/70 dark:text-amber-300/60 italic">
            This is not tax advice or a recommendation. Each trader is
            responsible for understanding wash sale rules and their tax
            obligations.{" "}
            <a
              href="https://www.irs.gov/publications/p550#en_US_2023_publink100010601"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white dark:hover:text-amber-200"
            >
              Read IRS Publication 550
            </a>{" "}
            for official guidance.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          sessionStorage.setItem(getDismissKey(), "1");
        }}
        className="text-white dark:text-amber-400 hover:text-amber-100 dark:hover:text-amber-200 p-1 flex-shrink-0"
        aria-label="Dismiss"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}
